import React from 'react';
import { motion } from 'framer-motion';
import { mockData } from '../data/mock';
import { Scissors, Clock, Heart, Shield } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.15 } })
};

const About = () => {
  const { about } = mockData;

  const highlights = [
    { icon: Scissors, label: "Précision artisanale" },
    { icon: Clock, label: "Service rapide" },
    { icon: Heart, label: "Passion du métier" },
    { icon: Shield, label: "Consultation gratuite" }
  ];

  return (
    <section id="about" className="py-24 sm:py-32 bg-[#F5F5F0]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            className="relative"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/e29e1e74-dfad-4ff0-a54e-5cee69b74787/images/b5e354f9cdb93ca755eb6af9af026eb54a620e15806bd44925a7c7649d9f6c7b.png"
                alt="Détail couture - Ciseaux dorés et tissu rose"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating accent */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-2xl bg-[#D4AF37]/10 -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#E8D5D1]/30 -z-10" />
          </motion.div>

          {/* Text content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={1}
          >
            <span 
              className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Notre Savoir-faire
            </span>
            
            <h2 
              className="mt-4 text-4xl sm:text-5xl tracking-tight leading-tight font-normal text-[#1C1917]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {about.title}
            </h2>
            
            <p 
              className="mt-6 text-lg leading-relaxed text-[#57534E]"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
            >
              {about.description}
            </p>
            
            {/* Highlights grid */}
            <div className="mt-10 grid grid-cols-2 gap-6">
              {highlights.map((item, i) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={i}
                    className="flex items-center gap-3 group"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i + 2}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <span 
                      className="text-sm font-medium text-[#1C1917]"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {item.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            {/* Quote */}
            <div className="mt-10 pl-6 border-l-2 border-[#D4AF37]/40">
              <p 
                className="text-lg italic text-[#57534E] leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                "Notre passion pour la couture et notre attention aux détails font la différence."
              </p>
              <p 
                className="mt-3 text-sm font-medium text-[#D4AF37]"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                L'équipe Baya Couture
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
